import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { DropdownOption } from 'src/components/ui/dropdown';
import { HelpAndSupportTabs } from 'src/content/HelpAndSupport/utils';
import useUpdateAccessRights from 'src/hooks/useUpdateAccessRights';
import { sidebarIcons } from 'src/layouts/SidebarV2/icons';
import { buildProfileRoutes } from 'src/layouts/SidebarV2/utils';
import { authSignOut } from 'src/redux/slices/auth';
import type { RootState } from 'src/redux/store';
import { useDispatch, useSelector } from 'src/redux/store';
import type { StringKeys } from 'src/types/base';
import { LOOKUP } from 'src/types/enum';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

export interface UseProfile {
  name: string;
  accountTypeLabel?: string;
  isSubmitting: boolean;
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  menuItems: DropdownOption[];
  isSupportOpen: boolean;
  closeSupport: () => void;
  isSuccessOpen: boolean;
  setIsSuccessOpen: (open: boolean) => void;
  closeSuccess: () => void;
  supportTab: string;
  setSupportTab: (tab: string) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  openSupport: () => void;
}

export const useProfile = (): UseProfile => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { lookups, userAppPermissions } = useSelector(
    (state: RootState) => state.data
  );
  const {
    user: { accountType, name, isNonTrialUser }
  } = useSelector((state: RootState) => state.auth);
  const { isSysAdminUser } = useUpdateAccessRights();

  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [supportTab, setSupportTab] = useState<string>(
    HelpAndSupportTabs.CONTACT_SUPPORT
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const routes = buildProfileRoutes(
    navigate,
    userAppPermissions as StringKeys,
    isNonTrialUser,
    isSysAdminUser
  );

  const menuItems: DropdownOption[] = [
    ...routes,
    {
      id: 'contact-support',
      label: T.helpSupport,
      icon: sidebarIcons.helpCircle,
      onClick: () => setIsSupportOpen(true)
    },
    {
      id: 'logout',
      label: T.logout,
      icon: sidebarIcons.logout,
      onClick: () => void dispatch(authSignOut())
    }
  ].map((item) => ({ ...item, label: t(item.label) }));

  const accountTypeLabel = lookups[LOOKUP.ACCOUNT_TYPE]?.find(
    (item) => item.code === accountType
  )?.value;

  const closeSupport = () => setIsSupportOpen(false);
  const closeSuccess = () => {
    setIsSuccessOpen(false);
    setSupportTab(HelpAndSupportTabs.CONTACT_SUPPORT);
  };

  return {
    name,
    accountTypeLabel,
    isSubmitting,
    isMenuOpen,
    setIsMenuOpen,
    menuItems,
    isSupportOpen,
    closeSupport,
    isSuccessOpen,
    setIsSuccessOpen,
    closeSuccess,
    supportTab,
    setSupportTab,
    setIsSubmitting,
    openSupport: () => setIsSupportOpen(true)
  };
};
